import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  Firestore,
} from 'firebase/firestore';

import type { PushSubscriptionData } from '../../../lib/push-notifications';

export interface UserSubscription {
  id: string;
  userId: string;
  subscription: PushSubscriptionData;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingSubscription {
  id: string;
  meetingId: string;
  userId: string;
  createdAt: string;
}

const subscriptionsCollection = (firestore: Firestore) =>
  collection(firestore, 'subscriptions');

const meetingSubscriptionsCollection = (firestore: Firestore) =>
  collection(firestore, 'meetingSubscriptions');

export const saveUserSubscription = async (
  firestore: Firestore,
  userId: string,
  subscriptionData: PushSubscriptionData,
): Promise<void> => {
  const subscriptionId = userId;
  const subscriptionRef = doc(subscriptionsCollection(firestore), subscriptionId);

  const now = new Date().toISOString();

  await setDoc(subscriptionRef, {
    id: subscriptionId,
    userId,
    subscription: subscriptionData,
    createdAt: now,
    updatedAt: now,
  });

  console.log('User subscription saved:', { userId, subscription: subscriptionData });
};

export const removeUserSubscription = async (
  firestore: Firestore,
  userId: string,
): Promise<void> => {
  const subscriptionId = userId;
  const subscriptionRef = doc(subscriptionsCollection(firestore), subscriptionId);

  await deleteDoc(subscriptionRef);
  console.log('User subscription removed:', { userId });
};

export const subscribeToMeetingNotifications = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
): Promise<void> => {
  const subscriptionId = `${meetingId}_${userId}`;
  const subscriptionRef = doc(meetingSubscriptionsCollection(firestore), subscriptionId);

  const now = new Date().toISOString();

  await setDoc(subscriptionRef, {
    id: subscriptionId,
    meetingId,
    userId,
    createdAt: now,
  });

  console.log('Meeting subscription created:', { meetingId, userId });
};

export const unsubscribeFromMeetingNotifications = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
): Promise<void> => {
  const subscriptionId = `${meetingId}_${userId}`;
  const subscriptionRef = doc(meetingSubscriptionsCollection(firestore), subscriptionId);

  await deleteDoc(subscriptionRef);
  console.log('Meeting subscription removed:', { meetingId, userId });
};

export const getUserMeetingSubscriptions = async (
  firestore: Firestore,
  userId: string,
): Promise<MeetingSubscription[]> => {
  const subscriptionsQuery = query(
    meetingSubscriptionsCollection(firestore),
    where('userId', '==', userId),
  );

  const snapshot = await getDocs(subscriptionsQuery);
  return snapshot.docs.map(doc => doc.data() as MeetingSubscription);
};

export const isMeetingSubscribed = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
): Promise<boolean> => {
  const subscriptionId = `${meetingId}_${userId}`;
  const subscriptionRef = doc(meetingSubscriptionsCollection(firestore), subscriptionId);

  try {
    const docSnapshot = await getDoc(subscriptionRef);
    return docSnapshot.exists();
  } catch (error) {
    console.error('Error checking meeting subscription:', error);
    return false;
  }
};
